import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Alert } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiPlus, mdiInformationOutline } from "@mdi/js";
import { useApp } from "../../../contexts/AppContext";
import { fetchNarratives, setCurrentNarrativePage } from "../slice";
import CustomPagination from "../../../components/common/Pagination";
import { useInitialFetch } from "../../../hooks/useInitialFetch";
import { usePartialLoading } from "../../../hooks/usePartialLoading";
import SmartNarrativeCard from "../../../components/common/SmartNarrativeCard";
import "./narrativeLibrary.css";

const NarrativeLibrary = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    user: { profile },
  } = useApp();

  const { narratives, getNarrativesStatus, currentNarrativePage } = useSelector(
    (state) => state.templates
  );

  const narrativesPerPage = 10;
  const narrativesLoading = usePartialLoading("narratives-content");
  const { startLoading, stopLoading } = narrativesLoading;

  // Fetch narratives on component mount
  useInitialFetch(
    () => {
      narrativesLoading.startLoading();
      dispatch(fetchNarratives(currentNarrativePage));
    },
    { skip: narratives.results.length > 0 }
  );

  useEffect(() => {
    if (getNarrativesStatus === "success" || getNarrativesStatus === "failed") {
      stopLoading();
    }
  }, [getNarrativesStatus, stopLoading]);

  const handlePageChange = (pageNumber) => {
    dispatch(setCurrentNarrativePage(pageNumber));
    startLoading();
    dispatch(fetchNarratives(pageNumber));
  };

  return (
    <div
      id="narratives-content"
      className="narrative-library-container"
      style={{ position: "relative", minHeight: "300px" }}
    >
      {/* Narrative Header */}
      {narratives.count > 0 && (
        <div className="narrative-header">
          <div className="narrative-count">
            <span className="count-number">{narratives.count}</span>
            <span className="count-text">
              Narratives available for AI reference
            </span>
          </div>
        </div>
      )}

      {/* Upgrade Banner for Free Users */}
      {profile?.subscription?.tier === "Free" && narratives.count > 0 && (
        <Alert variant="primary" className="narrative-upgrade-banner">
          <div className="banner-content">
            <Icon
              path={mdiInformationOutline}
              size={0.8}
              className="banner-icon"
            />
            <span>Upgrade to let AI use them to assist you.</span>
          </div>
        </Alert>
      )}

      {narratives.results.length === 0 && !narrativesLoading.isLoading ? (
        <div className="empty-state">
          <h4>No narratives in your library</h4>
          <p>Upload a template to add narratives.</p>
          <p>AI will reference them when generating results.</p>
          <Button variant="primary" onClick={() => navigate("/templates/new")}>
            <Icon path={mdiPlus} size={0.8} className="me-2" />
            New Template
          </Button>
        </div>
      ) : (
        <>
          <div className="narratives-grid">
            {narratives.results.map((narrative) => (
              <SmartNarrativeCard
                key={narrative.id}
                narrative={narrative}
                userTier={profile?.subscription?.tier}
              />
            ))}
          </div>

          {narratives.count > narrativesPerPage && (
            <div className="pagination-container">
              <CustomPagination
                totalItems={narratives.count}
                itemsPerPage={narrativesPerPage}
                currentPage={currentNarrativePage}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NarrativeLibrary;
